import { DOWNLOAD_FORMAT, type DownloadFormatType, type PublishProvidersType } from "hoxton-shared/enums/exports";

import { apolloClient } from "@/apollo/index";

import type { JobNotification } from "@/store/modules/jobs";

import type { ConstructedDeliverable, LibraryFilters } from "@/types/types";

import publishVariationsXmlFeedMutation from "@/apollo/mutations/PublishVariationsXmlFeed.gql";
import unpublishVariationsXmlFeedMutation from "@/apollo/mutations/UnpublishVariationsXmlFeed.gql";
import addDownloadComputedDeliverablesJobMutation from "@/apollo/mutations/AddDownloadComputedDeliverablesJob.gql";
import publishChoreographFeedMutation from "@/apollo/mutations/PublishChoreographFeed.gql";

import constructLibraryFiltersQueryVariables from "@/utils/constructLibraryFiltersQueryVariables";
import getComputedDeliverableIdentifiers from "@/services/deliverables/getComputedDeliverableIdentifiers";

function getBatchMutationVariables(
    campaignId: string,
    platform: DownloadFormatType | PublishProvidersType,
    deliverables: ConstructedDeliverable[],
    deliverableIdentifiers: string[],
    libraryFilters?: LibraryFilters,
    platformOpts?: Record<string, unknown>
) {
    const variables = {
        campaignId,
        platform,
        platformOpts
    } as {
        campaignId: string;
        platform: string;
        platformOpts: Record<string, unknown>;
        libraryFilters?: ReturnType<typeof constructLibraryFiltersQueryVariables>;
        computedDeliverableIdentifiers?: string[];
    };

    if (libraryFilters && Object.keys(libraryFilters).length > 0) {
        // all selected modes
        const libraryFiltersVars = constructLibraryFiltersQueryVariables(libraryFilters);
        variables.libraryFilters = libraryFiltersVars;
    } else if (deliverableIdentifiers.length) {
        variables.computedDeliverableIdentifiers = deliverableIdentifiers;
    } else {
        const computedDeliverableIdentifiers = getComputedDeliverableIdentifiers(deliverables);
        variables.computedDeliverableIdentifiers = computedDeliverableIdentifiers;
    }

    return variables;
}

export async function publishDeliverables(
    campaignId: string,
    platform: PublishProvidersType,
    deliverables: ConstructedDeliverable[],
    libraryFilters?: LibraryFilters,
    rebuild = false
) {
    const variables = getBatchMutationVariables(campaignId, platform, deliverables, [], libraryFilters);

    const { data } = await apolloClient.mutate<{
        publishVariationsXmlFeed: JobNotification;
    }>({
        mutation: publishVariationsXmlFeedMutation,
        variables: { ...variables, rebuild }
    });

    if (!data?.publishVariationsXmlFeed) {
        throw new Error("Failed to publish variations");
    }

    return data.publishVariationsXmlFeed;
}

export async function unpublishDeliverables(
    campaignId: string,
    platform: PublishProvidersType,
    deliverables: ConstructedDeliverable[],
    libraryFilters?: LibraryFilters
) {
    const variables = getBatchMutationVariables(campaignId, platform, deliverables, [], libraryFilters);

    try {
        const { data } = await apolloClient.mutate<{
            unpublishVariationsXmlFeed: JobNotification;
        }>({
            mutation: unpublishVariationsXmlFeedMutation,
            variables
        });

        return data?.unpublishVariationsXmlFeed;
    } catch (error) {
        console.error("Error unpublishing variations:", error);
        throw error;
    }
}

export async function downloadJob(
    campaignId: string,
    platform: DownloadFormatType,
    deliverables: ConstructedDeliverable[],
    deliverableIdentifiers: string[] = [],
    libraryFilters?: LibraryFilters,
    platformOpts = {}
) {
    const variables: ReturnType<typeof getBatchMutationVariables> & { deviceScaleFactor?: number } =
        getBatchMutationVariables(
            campaignId,
            platform,
            deliverables,
            deliverableIdentifiers,
            libraryFilters,
            platformOpts
        );

    if (platform === DOWNLOAD_FORMAT.IMAGE_NO_SCALE) {
        variables.platform = "image";
        variables.deviceScaleFactor = 1;
    } else if (platform === DOWNLOAD_FORMAT.IMAGE_JPEG_NO_SCALE || platform === DOWNLOAD_FORMAT.IMAGE_JPEG) {
        variables.platform = "image";
        variables.platformOpts.format = "jpeg";

        if (platform === DOWNLOAD_FORMAT.IMAGE_JPEG_NO_SCALE) {
            variables.deviceScaleFactor = 1;
        }
    }

    if (platform === DOWNLOAD_FORMAT.IMAGE || platform === DOWNLOAD_FORMAT.IMAGE_JPEG) {
        variables.deviceScaleFactor = 2;
    }

    const { data } = await apolloClient.mutate<{
        addDownloadComputedDeliverablesJob: JobNotification;
    }>({
        mutation: addDownloadComputedDeliverablesJobMutation,
        variables
    });

    return data!.addDownloadComputedDeliverablesJob;
}

export async function publishChoreographFeed(jobId: string, uniqueIdsList: string[]) {
    const { data } = await apolloClient.mutate<{
        publishChoreographFeed: JobNotification;
    }>({
        mutation: publishChoreographFeedMutation,
        variables: { jobId, uniqueIdsList }
    });
    return data!.publishChoreographFeed;
}
